import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CLAUDE_CHANNEL_ENVELOPE_VERSION,
  ClaudeChatNamespace,
  type ApprovalCard,
  type CardClosedEnvelope,
  type CardEnvelope,
  type ChannelErrorCode,
  type ChannelErrorEnvelope,
  type ChannelSessionDescriptor,
  type ChannelVerdictBehavior,
  type ClaudeChannelConversation,
  type ClaudeChannelMessage,
  type ClaudeChannelMessageState,
  type ConversationOpenAck,
  type DrawerMessageEnvelope,
  type MessageSendAck,
  type MessageSendRefusal,
  type MessageStateEnvelope,
  type PageContext,
  type SessionGoneEnvelope,
  type SessionsEnvelope,
} from "common";
import { connectSocket } from "@via/utils/socket";
import {
  claudeChannelKeys,
  fetchClaudeChannelMessages,
  useClaudeChannelConversations,
} from "@via/api/claude-channel.api";

const V = CLAUDE_CHANNEL_ENVELOPE_VERSION;

/** A card the drawer shows. `outcome: "unknown"` means the session moved on before anyone answered here. */
export interface ClaudeChannelCardView extends ApprovalCard {
  outcome?: "unknown";
}

/**
 * Cards this browser tab dismissed. `card:dismiss` closes a card in the dismissing tab only, and
 * `sessions:list` resends every pending card, so the tab has to remember what it dismissed. Kept for
 * the life of the tab (module scope), across drawer close and reopen.
 */
const dismissedCards = new Set<string>();
const cardKey = (sessionId: string, requestId: string) =>
  `${sessionId}:${requestId}`;

/** How long a send waits for the backend's acknowledgement before the drawer calls it unconfirmed. */
export const SEND_ACK_TIMEOUT_MS = 15_000;

/**
 * The outcome of a send. `disconnected`: the drawer had no connection, nothing was emitted.
 * `no_answer`: emitted, but no acknowledgement came in time, so it may or may not have arrived.
 */
export type ClaudeChannelSendResult =
  | { ok: true }
  | {
      ok: false;
      reason: MessageSendRefusal | "disconnected" | "no_answer";
    };

export interface UseClaudeChannel {
  sessions: ChannelSessionDescriptor[];
  conversations: ClaudeChannelConversation[];
  messages: Record<number, ClaudeChannelMessage[]>;
  cards: Record<string, ClaudeChannelCardView[]>;
  connected: boolean;
  /** Set when the backend ended the connection, for example `auth_expired`. */
  ended: ChannelErrorCode | null;
  /** Null when the session is not live, the drawer is disconnected, or no answer came in time. */
  openConversation: (
    sessionId: string,
    title: string
  ) => Promise<ClaudeChannelConversation | null>;
  loadMessages: (conversationId: number) => Promise<void>;
  /** Resolves once the backend accepted or refused the message; the caller keeps the text until ok. */
  send: (
    conversationId: number,
    text: string,
    context?: PageContext
  ) => Promise<ClaudeChannelSendResult>;
  verdict: (
    sessionId: string,
    requestId: string,
    behavior: ChannelVerdictBehavior
  ) => void;
  dismiss: (sessionId: string, requestId: string) => void;
  /** Removes a card already closed as outcome unknown from this tab. */
  clearClosedCard: (sessionId: string, requestId: string) => void;
  reconnect: () => void;
}

/** A message only moves forward: once delivered or failed it never goes back to queued. */
const STATE_RANK: Record<ClaudeChannelMessageState, number> = {
  queued: 0,
  delivered: 1,
  failed: 1,
};

/**
 * Merges history and live messages by id (#1404). A history response can start before a reply's
 * `message:state` and finish after it, so a merge keeps the further state instead of letting the
 * incoming row win. A reply from Claude also means every earlier queued message in the
 * conversation reached the session, even if its `message:state` never reached this tab.
 */
const mergeMessages = (
  existing: ClaudeChannelMessage[] = [],
  incoming: ClaudeChannelMessage[]
): ClaudeChannelMessage[] => {
  const byId = new Map(existing.map(m => [m.id, m]));
  for (const m of incoming) {
    const prev = byId.get(m.id);
    const state =
      prev && STATE_RANK[prev.state] > STATE_RANK[m.state]
        ? prev.state
        : m.state;
    byId.set(m.id, { ...prev, ...m, state });
  }
  const merged = Array.from(byId.values()).sort((a, b) => a.id - b.id);
  const lastReply = merged.reduce(
    (max, m) => (m.direction === "from_claude" ? Math.max(max, m.id) : max),
    -Infinity
  );
  return merged.map(m =>
    m.direction === "to_claude" && m.state === "queued" && m.id < lastReply
      ? { ...m, state: "delivered" as const }
      : m
  );
};

/**
 * The drawer side of the Claude Code channel (agent-6-2). Its own socket to `/claude-chat`, on the
 * browser session cookie. It never reads an AI integration credential or a model setting.
 */
export const useClaudeChannel = (): UseClaudeChannel => {
  const queryClient = useQueryClient();
  const socketRef = useRef(connectSocket(ClaudeChatNamespace));
  const [connected, setConnected] = useState(socketRef.current.connected);
  const [ended, setEnded] = useState<ChannelErrorCode | null>(null);
  const [sessions, setSessions] = useState<ChannelSessionDescriptor[]>([]);
  const [messages, setMessages] = useState<
    Record<number, ClaudeChannelMessage[]>
  >({});
  const [cards, setCards] = useState<Record<string, ClaudeChannelCardView[]>>(
    {}
  );
  const { data: conversations = [] } = useClaudeChannelConversations();
  /** Conversations whose history this tab loaded, reloaded on every reconnect. */
  const loadedRef = useRef(new Set<number>());

  const loadMessages = useCallback(async (conversationId: number) => {
    loadedRef.current.add(conversationId);
    const loaded = await fetchClaudeChannelMessages(conversationId);
    setMessages(prev => ({
      ...prev,
      [conversationId]: mergeMessages(prev[conversationId], loaded),
    }));
  }, []);

  useEffect(() => {
    const socket = socketRef.current;

    const onConnect = () => {
      setConnected(true);
      setEnded(null);
      socket.emit("sessions:list", { v: V });
      // A `message:state` or reply sent while this tab was disconnected is not replayed (#1404).
      for (const id of loadedRef.current)
        loadMessages(id).catch(() => undefined);
    };
    const onDisconnect = () => setConnected(false);
    const onError = (e: ChannelErrorEnvelope) => {
      if (e && typeof e.code === "string") setEnded(e.code);
    };
    // socket.io never retries a refusal by the server's middleware (an expired sign in, the flag
    // turned off) and leaves the socket inactive. A transport error it will retry keeps it active.
    const onConnectError = () => {
      if (!socket.active) setEnded("auth_expired");
    };
    const onSessions = (e: SessionsEnvelope) => {
      setSessions(e.sessions);
      const live = new Set(e.sessions.map(s => s.sessionId));
      setCards(prev =>
        Object.fromEntries(Object.entries(prev).filter(([id]) => live.has(id)))
      );
    };
    const onSessionGone = (e: SessionGoneEnvelope) =>
      setSessions(prev => prev.filter(s => s.sessionId !== e.sessionId));
    const onMessage = (e: DrawerMessageEnvelope) =>
      setMessages(prev => ({
        ...prev,
        [e.conversationId]: mergeMessages(prev[e.conversationId], [e.message]),
      }));
    const onMessageState = (e: MessageStateEnvelope) =>
      setMessages(prev => ({
        ...prev,
        [e.conversationId]: (prev[e.conversationId] ?? []).map(m =>
          m.direction === "to_claude" && m.state === "queued"
            ? { ...m, state: e.state }
            : m
        ),
      }));
    const onCard = (e: CardEnvelope) => {
      if (dismissedCards.has(cardKey(e.sessionId, e.card.requestId))) return;
      setCards(prev => {
        const list = (prev[e.sessionId] ?? []).filter(
          c => c.requestId !== e.card.requestId
        );
        return { ...prev, [e.sessionId]: [...list, e.card] };
      });
    };
    const onCardClosed = (e: CardClosedEnvelope) => {
      const dismissed = dismissedCards.has(cardKey(e.sessionId, e.requestId));
      setCards(prev => {
        const list = prev[e.sessionId] ?? [];
        const next =
          e.answered || dismissed
            ? list.filter(c => c.requestId !== e.requestId)
            : list.map(c =>
                c.requestId === e.requestId
                  ? { ...c, outcome: "unknown" as const }
                  : c
              );
        return { ...prev, [e.sessionId]: next };
      });
      void queryClient.invalidateQueries({
        queryKey: ["claude-channel", "approvals"],
      });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("error", onError);
    socket.on("connect_error", onConnectError);
    socket.on("sessions", onSessions);
    socket.on("session:gone", onSessionGone);
    socket.on("message", onMessage);
    socket.on("message:state", onMessageState);
    socket.on("card", onCard);
    socket.on("card:closed", onCardClosed);

    if (socket.connected) onConnect();
    else if (!socket.active) socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("error", onError);
      socket.off("connect_error", onConnectError);
      socket.off("sessions", onSessions);
      socket.off("session:gone", onSessionGone);
      socket.off("message", onMessage);
      socket.off("message:state", onMessageState);
      socket.off("card", onCard);
      socket.off("card:closed", onCardClosed);
    };
  }, [queryClient, loadMessages]);

  const openConversation = useCallback(
    (sessionId: string, title: string) =>
      new Promise<ClaudeChannelConversation | null>(resolve => {
        const socket = socketRef.current;
        if (!socket.connected) {
          resolve(null);
          return;
        }
        socket
          .timeout(SEND_ACK_TIMEOUT_MS)
          .emit(
            "conversation:open",
            { v: V, sessionId, title },
            (err: Error | null, ack?: ConversationOpenAck) => {
              const conversation = (!err && ack?.conversation) || null;
              if (conversation) {
                queryClient.setQueryData<ClaudeChannelConversation[]>(
                  claudeChannelKeys.conversations,
                  prev => [conversation, ...(prev ?? [])]
                );
              }
              resolve(conversation);
            }
          );
      }),
    [queryClient]
  );

  const send = useCallback(
    (conversationId: number, text: string, context?: PageContext) =>
      new Promise<ClaudeChannelSendResult>(resolve => {
        const socket = socketRef.current;
        // socket.io would buffer the emit until a reconnect that may never come.
        if (!socket.connected) {
          resolve({ ok: false, reason: "disconnected" });
          return;
        }
        socket
          .timeout(SEND_ACK_TIMEOUT_MS)
          .emit(
            "message:send",
            { v: V, conversationId, text, ...(context ? { context } : {}) },
            (err: Error | null, ack?: MessageSendAck) => {
              if (err || !ack) resolve({ ok: false, reason: "no_answer" });
              else if (ack.ok) resolve({ ok: true });
              else {
                if (ack.reason === "auth_expired") setEnded("auth_expired");
                resolve({ ok: false, reason: ack.reason });
              }
            }
          );
      }),
    []
  );

  const verdict = useCallback(
    (
      sessionId: string,
      requestId: string,
      behavior: ChannelVerdictBehavior
    ) => {
      socketRef.current.emit("verdict", {
        v: V,
        sessionId,
        requestId,
        behavior,
      });
    },
    []
  );

  const removeCard = (sessionId: string, requestId: string) =>
    setCards(prev => ({
      ...prev,
      [sessionId]: (prev[sessionId] ?? []).filter(
        c => c.requestId !== requestId
      ),
    }));

  const dismiss = useCallback((sessionId: string, requestId: string) => {
    dismissedCards.add(cardKey(sessionId, requestId));
    removeCard(sessionId, requestId);
    socketRef.current.emit("card:dismiss", { v: V, sessionId, requestId });
  }, []);

  const clearClosedCard = useCallback(
    (sessionId: string, requestId: string) => removeCard(sessionId, requestId),
    []
  );

  const reconnect = useCallback(() => {
    if (!socketRef.current.connected) socketRef.current.connect();
  }, []);

  return {
    sessions,
    conversations,
    messages,
    cards,
    connected,
    ended,
    openConversation,
    loadMessages,
    send,
    verdict,
    dismiss,
    clearClosedCard,
    reconnect,
  };
};
